import {useMutation} from '@tanstack/react-query';
import React, {FC, useCallback, useMemo, useState} from 'react';

import {renderLanguageCode} from '../../../../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {AiSupportedLanguageCodesEnum} from '../../../../../../../libs/shared/core/src/helpers/ai/constants';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {useInitialEffect} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {LoadedMessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {ApiMessageTranslation} from '../../../../../../../libs/shared/core/src/models/api/messageTranslationModel';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {postApiAsync} from '../../../../../../../libs/shared/core/src/services/http/http';
import {isHttpTooManyRequestsError} from '../../../../../../../libs/shared/core/src/services/http/httpError';
import {parseRateLimitHeaders} from '../../../../../../../libs/shared/core/src/services/http/rateLimitError';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {getIsAiTranslateEnabled} from '../../../../../../store/data/company/companySelectors';
import {updatePreferredLanguagesAction} from '../../../../../../store/data/preferences/preferencesActions';
import {getPreferredLanguages} from '../../../../../../store/data/preferences/preferencesSelectors';
import {AiTranslateSettingsDropdown} from './aiTranslateSettingsDropdown';
import {shouldTranslateLanguage} from './helpers';
import {MessageViewerTranslation} from './messageViewerTranslation';

/*
 * Props.
 */

export interface MessageViewerTranslationContainerProps {
  message: LoadedMessageModel;
  className?: string;
}

/*
 * Component.
 */

export const MessageViewerTranslationContainer: FC<MessageViewerTranslationContainerProps> = ({
  className,
  message,
}) => {
  const [isTranslationOpen, setIsTranslationOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();

  const preferredLanguages = useAppSelector(getPreferredLanguages);
  const isAiTranslateEnabled = useAppSelector(getIsAiTranslateEnabled);

  // preferredLanguages guaranteed to contain at least one language.
  const preferredLanguage = preferredLanguages[0];

  const trackingData = useMemo(
    () => ({
      message_id: message.id,
      conversation_id: message.conversationId,
      from_language: message.detectedLanguage,
      to_language: preferredLanguage,
    }),
    [message, preferredLanguage],
  );

  const shouldShowBanner =
    isAiTranslateEnabled && message.detectedLanguage && shouldTranslateLanguage(message, preferredLanguages);

  useInitialEffect(() => {
    if (shouldShowBanner) {
      trackDataEvent(DataEventActionsEnum.VIEW, ViewerDataEventObject.MESSAGE_TRANSLATION, trackingData);
    }
  });

  const onToggleTranslationOpen = () => {
    trackDataEvent(
      isTranslationOpen ? DataEventActionsEnum.COLLAPSE : DataEventActionsEnum.EXPAND,
      ViewerDataEventObject.MESSAGE_TRANSLATION,
      trackingData,
    );

    setIsTranslationOpen((current) => !current);
  };

  const translateMutation = useMutation({
    mutationKey: ['translate', message.messageUrl],
    mutationFn: async (data: {language: AiSupportedLanguageCodesEnum}) => {
      trackDataEvent(DataEventActionsEnum.CLICK, ViewerDataEventObject.MESSAGE_TRANSLATION, trackingData);

      return postApiAsync(ApiMessageTranslation, `${message.messageUrl}/translate`, {
        language: data.language,
      });
    },
    onSettled: () => setIsTranslationOpen(true),
  });

  const onSavePreferredLanguages = useCallback(
    (newPreferredLanguages: ReadonlyArray<AiSupportedLanguageCodesEnum>) => {
      trackDataEvent(DataEventActionsEnum.SUCCESS, ViewerDataEventObject.MESSAGE_TRANSLATION_SETTINGS, {
        message_id: message.id,
        conversation_id: message.conversationId,
        translate_to: newPreferredLanguages[0],
        never_translate: newPreferredLanguages,
      });

      const newPreferredLanguage = newPreferredLanguages[0];

      // We reset the mutation if the preferred language changes.
      if (newPreferredLanguage !== preferredLanguage) {
        translateMutation.mutate({language: newPreferredLanguage});
      }

      dispatch(updatePreferredLanguagesAction(newPreferredLanguages));
    },
    [preferredLanguage, translateMutation, dispatch, message, trackDataEvent],
  );

  if (!shouldShowBanner) {
    return null;
  }

  const translationError = getTranslationError(message.detectedLanguage, translateMutation.error);

  return (
    <MessageViewerTranslation
      className={className}
      detectedLanguage={message.detectedLanguage}
      translation={translateMutation.data}
      error={translationError}
      onTranslate={() => translateMutation.mutate({language: preferredLanguage})}
      onCancel={() => translateMutation.reset()}
      isLoading={translateMutation.isLoading}
      isExpanded={isTranslationOpen}
      onToggleExpanded={onToggleTranslationOpen}
      renderSettingsDropdown={(dropdownProps) => (
        <AiTranslateSettingsDropdown
          dropdownProps={{...dropdownProps, width: 300}}
          preferredLanguages={preferredLanguages}
          onSave={(value) => {
            onSavePreferredLanguages(value);
            dropdownProps.onRequestClose?.();
          }}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

function getTranslationError(language: AiSupportedLanguageCodesEnum, error: unknown) {
  if (!error) {
    return undefined;
  }

  if (isHttpTooManyRequestsError(error)) {
    const rateLimitInfo = parseRateLimitHeaders(error.headers);
    if (rateLimitInfo?.limit) {
      return {
        title: <Trans id="message-viewer-translation-banner-title-rate-limit-error">Limit reached</Trans>,
        message: (
          <Trans id="message-viewer-translation-banner-message-rate-limit-error">
            You can generate up to {rateLimitInfo.limit} translations per day. Please try again tomorrow.
          </Trans>
        ),
      };
    }

    return {
      title: (
        <Trans id="message-viewer-translation-banner-title-rate-limit-unknown-error">Limit reached</Trans>
      ),
      message: (
        <Trans id="message-viewer-translation-banner-message-rate-limit-unknown-error">
          You cannot generate any more translations today. Please try again tomorrow.
        </Trans>
      ),
    };
  }

  return {
    title: (
      <Trans id="message-viewer-translation-banner-title-error">
        Failed to translate to {renderLanguageCode(language)}
      </Trans>
    ),
    message: (
      <Trans id="message-viewer-translation-banner-title-error">
        A translation couldn&apos;t be generated for this message.
      </Trans>
    ),
  };
}
